import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/Button/Button.tsx";
import { Skeleton } from "../../../../components/Skeleton/Skeleton.tsx";
import { useDebouncedLoading } from "../../../../hooks/useDebouncedLoading.ts";
import { HOME_PROJECTS } from "../../../../routes.ts";
import { trackEvent } from "../../../../tracking/tracking.ts";
import { useRedirectToBoard } from "../../../hooks/useRedirectToBoard.ts";
import { useBoards } from "../hooks/useBoards.ts";
import { BoardTile } from "./BoardTile.tsx";

export const RecentBoardsSection = () => {
  const [itemsPerRow, setItemsPerRow] = useState(4); // Default estimation
  const gridRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const redirectToBoard = useRedirectToBoard();
  const { userBoards, isLoading } = useBoards();

  // XXX: We need to debounce the loading state to avoid flickering when entering board with no recent projects
  const debouncedIsLoading = useDebouncedLoading({ isLoading, delay: 100 });

  // XXX: Calculate how many items fit in one row based on container width
  useEffect(() => {
    const calculateItemsInRow = () => {
      if (gridRef.current) {
        const gridWidth = gridRef.current.getBoundingClientRect().width;
        const itemMinWidth = 240;
        const gap = 16;
        const itemsCount = Math.floor((gridWidth + gap) / (itemMinWidth + gap));
        setItemsPerRow(Math.max(1, itemsCount));
      }
    };
    calculateItemsInRow();
    window.addEventListener("resize", calculateItemsInRow);

    return () => window.removeEventListener("resize", calculateItemsInRow);
  }, [isLoading]);

  return (
    ((userBoards && userBoards.length > 0) ?? debouncedIsLoading) && (
      <div className="flex-col">
        <div className="min-h-1600 py-400 flex-row items-center gap-400">
          <div className="heading-2xl">Recent projects</div>
          <Button
            variant="tertiary"
            size="md"
            onClick={() => navigate(HOME_PROJECTS)}
          >
            See all
          </Button>
        </div>
        <div className="w-full flex-col items-center">
          <div
            ref={gridRef}
            className="w-full grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-400"
          >
            {debouncedIsLoading || !userBoards || userBoards.length === 0
              ? Array.from({ length: itemsPerRow }).map((_, index) => (
                  <Skeleton
                    key={index}
                    className="h-[250px] w-full rounded-100"
                  />
                ))
              : userBoards
                  .sortDesc((board) => board.edited_at)
                  .slice(0, itemsPerRow)
                  .map((board) => (
                    <BoardTile
                      key={board.uuid}
                      board={board}
                      onClick={() => {
                        trackEvent("project:open", {
                          project_uuid: board.uuid,
                        });
                        redirectToBoard({
                          uuid: board.uuid,
                          boardStatePreset: "blank",
                        });
                      }}
                    />
                  ))}
          </div>
        </div>
      </div>
    )
  );
};
